import { createShortUid, logError } from '@amazon/vinyl-util'
import { ctx } from '../env/playbackContext'
import type { AudiobookFields } from '../gql/audiobookFieldsFrag'
import { getPrimaryImage } from '../util/imageUtil'
import type { PlaylistEntry } from './PlaylistController'

const { target } = ctx('audiobookToPlaylistEntry')

/**
 * Convert an `Audiobook` GraphQL node into a `PlaylistEntry`. Audiobooks play
 * on the Audible engine, which the delegate routes to via
 * `entry.entity.__typename` (see `DelegatingPlayer.select`) and loads from the
 * entity's asin — the Vinyl-shaped `trackLoadOptions` here is a placeholder the
 * Audible sub-player never consumes, present only to satisfy the required
 * field. Returns null when there's no playable asin.
 */
export function audiobookToPlaylistEntry(audiobook: AudiobookFields): PlaylistEntry | null {
    const asin = audiobook.playParams?.id
    if (!asin) {
        logError(target, 'audiobook missing playParams.id', audiobook.id)
        return null
    }

    const image = getPrimaryImage(audiobook.images)

    return {
        id: audiobook.id ?? createShortUid(),
        entity: audiobook,
        title: audiobook.title ?? '',
        albumTitle: '',
        artistName: audiobook.authors?.[0]?.name ?? '',
        duration: 0,
        image,
        // Audible-engine-only content: the delegate routes on __typename and
        // loads via the asin, so these Vinyl options are never played.
        trackLoadOptions: { type: 'asin', uri: `asin://${asin}` },
    }
}
